import type { DropRoute, OptionProfile, WorldData } from '../types/world'

/*
 * Calidad de una recompensa calculada desde los perfiles documentados:
 * 21_bag_rewards.variant_id → 22_bag_reward_variants (option_profiles) / 23_bag_variant_profiles → 25_option_profiles.
 * La distribución de 25 está CONDICIONADA a que salga ese resultado de la bolsa: un excellent_count = 1
 * con peso 100 % significa "si sale este ítem, sale Excellent", NO 100 % de probabilidad por muerte.
 * Una calidad vacía en la ruta no se convierte en "normal": si no hay perfil, queda "sin documentar".
 */

export type RouteQualityKind = 'normal' | 'excellent' | 'ancient' | 'other'

export type ProfileQuality = {
  quality: 'excellent' | 'normal' | 'mixed'
  excellentMin: number
  excellentMax: number
  /** Claves de perfil usadas (p. ej. "4:12"). */
  profileKeys: string[]
  rewardVariantIds: string[]
}

export type EffectiveQuality = {
  quality: RouteQualityKind
  source: 'ruta' | 'perfil' | 'sin documentar'
  /** true: calidad condicionada a que salga ese resultado (perfil), no probabilidad por muerte. */
  conditional: boolean
  profile?: ProfileQuality
  note: string
}

const keyOf = (type: string | undefined, profile: string) => (profile.includes(':') ? profile : type ? `${type}:${profile}` : profile)
const isExcellentOption = (o: OptionProfile) => /excel/i.test(o.optionName ?? '') || /excel/i.test(o.optionType ?? '')

const caches = new WeakMap<WorldData, { variant: Map<string, ProfileQuality | null>; route: Map<string, EffectiveQuality> }>()
function cacheFor(w: WorldData) {
  let c = caches.get(w)
  if (!c) {
    c = { variant: new Map(), route: new Map() }
    caches.set(w, c)
  }
  return c
}

/** Claves de perfil de una variante de recompensa (22 + 23). */
export function rewardVariantProfileKeys(w: WorldData, rewardVariantId: string): string[] {
  const keys = new Set(w.bagRewardVariants.get(rewardVariantId)?.profileKeys ?? [])
  w.bagVariantProfiles.filter((p) => p.variantId === rewardVariantId).forEach((p) => keys.add(keyOf(p.optionType, p.profileId)))
  return Array.from(keys)
}

/** excellent_count resuelto desde 25_option_profiles para una variante de recompensa. */
export function rewardVariantQuality(w: WorldData, rewardVariantId: string): ProfileQuality | undefined {
  const c = cacheFor(w).variant
  if (c.has(rewardVariantId)) return c.get(rewardVariantId) ?? undefined
  const keys = rewardVariantProfileKeys(w, rewardVariantId)
  const keySet = new Set(keys)
  const bareIds = new Set(keys.map((k) => k.split(':').pop()!))
  const profiles = w.optionProfiles.filter(
    (o) => o.usedByRuntime !== false && isExcellentOption(o) && (keySet.has(o.id) || keySet.has(keyOf(o.optionType, o.profileId)) || keySet.has(o.profileId) || (!o.optionType && bareIds.has(o.profileId))),
  )
  const outcomes = profiles.flatMap((o) => (o.weights.length ? o.outcomes.filter((_, i) => (o.weights[i] ?? 0) > 0) : o.outcomes))
  if (!outcomes.length) {
    c.set(rewardVariantId, null)
    return undefined
  }
  const min = Math.min(...outcomes)
  const max = Math.max(...outcomes)
  const out: ProfileQuality = { quality: min >= 1 ? 'excellent' : max === 0 ? 'normal' : 'mixed', excellentMin: min, excellentMax: max, profileKeys: keys, rewardVariantIds: [rewardVariantId] }
  c.set(rewardVariantId, out)
  return out
}

function textQuality(q: string): RouteQualityKind {
  if (q === 'normal' || q === 'common') return 'normal'
  if (q.startsWith('exc')) return 'excellent'
  if (q.startsWith('anc')) return 'ancient'
  return 'other'
}

/** Calidad efectiva de una ruta: la declarada en la ruta o, si está vacía, la de los perfiles de su bolsa. */
export function effectiveRouteQuality(w: WorldData, r: DropRoute): EffectiveQuality {
  const c = cacheFor(w).route
  const hit = c.get(r.id)
  if (hit) return hit
  const q = (r.quality ?? '').toLowerCase().trim()
  let out: EffectiveQuality
  if (q) {
    out = { quality: textQuality(q), source: 'ruta', conditional: false, note: `Calidad declarada en la ruta: ${r.quality}` }
  } else {
    const baseId = r.itemId ?? r.variantId?.split('@')[0]
    // Recompensas de ESTE ítem dentro de la bolsa (ítem → pool → variante → perfil); el perfil de una variante no se extiende a la bolsa.
    const rewards = r.bagId ? w.bagRewards.filter((b) => b.bagId === r.bagId && b.usedByRuntime !== false && b.itemId === baseId && b.variantId) : []
    const variantIds = Array.from(new Set(rewards.map((b) => b.variantId!)))
    const poolIds = Array.from(new Set(rewards.map((b) => b.poolId).filter((x): x is string => !!x)))
    const quals = variantIds.map((id) => rewardVariantQuality(w, id)).filter((p): p is ProfileQuality => !!p)
    if (quals.length && quals.length === variantIds.length) {
      const all = (k: ProfileQuality['quality']) => quals.every((p) => p.quality === k)
      const quality: RouteQualityKind = all('excellent') ? 'excellent' : all('normal') ? 'normal' : 'other'
      const profile: ProfileQuality = {
        quality: all('excellent') ? 'excellent' : all('normal') ? 'normal' : 'mixed',
        excellentMin: Math.min(...quals.map((p) => p.excellentMin)),
        excellentMax: Math.max(...quals.map((p) => p.excellentMax)),
        profileKeys: Array.from(new Set(quals.flatMap((p) => p.profileKeys))),
        rewardVariantIds: variantIds,
      }
      out = {
        quality,
        source: 'perfil',
        conditional: true,
        profile,
        note: `Calidad calculada desde el perfil ${profile.profileKeys.join(', ')} (bolsa ${r.bagId} → ítem ${baseId}${poolIds.length ? ` · pool ${poolIds.join(', ')}` : ''} → variante ${variantIds.join(', ')}; solo las recompensas de este ítem, no toda la bolsa): excellent_count ${profile.excellentMin === profile.excellentMax ? profile.excellentMin : `${profile.excellentMin}–${profile.excellentMax}`}. Condicionado a que salga este resultado; no es una probabilidad por muerte.`,
      }
    } else {
      out = { quality: 'normal', source: 'sin documentar', conditional: false, note: 'Calidad vacía en la ruta y sin perfil de opciones documentado: calidad no documentada (no se asume normal).' }
    }
  }
  c.set(r.id, out)
  return out
}
